// IELTS Mock Test page component

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ieltsAPI } from '../services/api';
import { IELTSMockTest, IELTSSection, IELTSQuestion, IELTSTestSubmission, IELTSSectionSubmission, IELTSUserAnswerSubmission } from '../types';

const IELTSTest: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [test, setTest] = useState<IELTSMockTest | null>(null);
  const [currentSection, setCurrentSection] = useState<IELTSSection | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<IELTSQuestion | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: number]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testResultId, setTestResultId] = useState<number | null>(null);

  useEffect(() => {
    if (testId) {
      loadTest(parseInt(testId));
    }
  }, [testId]);

  useEffect(() => {
    if (currentSection) {
      setTimeLeft(currentSection.time_limit_minutes * 60);
    }
  }, [currentSection]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && currentSection) {
      // Time's up for current section
      handleNextSection();
    }
  }, [timeLeft]);

  const loadTest = async (id: number) => {
    try {
      const testData = await ieltsAPI.getTest(id);
      setTest(testData);
      
      if (testData.sections.length > 0) {
        setCurrentSection(testData.sections[0]);
        setCurrentQuestion(testData.sections[0].questions[0]);
        setCurrentQuestionIndex(0);
        setCurrentSectionIndex(0);
      }
    } catch (err: any) {
      setError('Failed to load test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNextQuestion = () => {
    if (!currentSection || !test) return;

    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < currentSection.questions.length) {
      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestion(currentSection.questions[nextIndex]);
    } else {
      // Move to next section
      handleNextSection();
    }
  };

  const handlePreviousQuestion = () => {
    if (!currentSection) return;

    const prevIndex = currentQuestionIndex - 1;
    if (prevIndex >= 0) {
      setCurrentQuestionIndex(prevIndex);
      setCurrentQuestion(currentSection.questions[prevIndex]);
    }
  };

  const handleNextSection = () => {
    if (!test) return;

    const nextSectionIndex = currentSectionIndex + 1;
    if (nextSectionIndex < test.sections.length) {
      setCurrentSectionIndex(nextSectionIndex);
      setCurrentSection(test.sections[nextSectionIndex]);
      setCurrentQuestion(test.sections[nextSectionIndex].questions[0]);
      setCurrentQuestionIndex(0);
    } else {
      // All sections completed, submit test
      handleSubmitTest();
    }
  };

  const handleSubmitTest = async () => {
    if (!test) return;

    setIsSubmitting(true);
    try {
      // Prepare submission data
      const sections: IELTSSectionSubmission[] = test.sections.map(section => ({
        section_id: section.id,
        answers: section.questions.map(question => ({
          question_id: question.id,
          user_answer: answers[question.id] || ''
        }))
      }));

      const submission: IELTSTestSubmission = {
        mock_test_id: test.id,
        sections
      };

      const result = await ieltsAPI.submitTest(submission);
      navigate(`/ielts/results/${result.id}`);
    } catch (err: any) {
      setError('Failed to submit test. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSectionIcon = (sectionType: string) => {
    switch (sectionType) {
      case 'listening': return '🎧';
      case 'reading': return '📖';
      case 'writing': return '✍️';
      case 'speaking': return '🎤';
      default: return '📝';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  if (!test || !currentSection || !currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Test Not Found</h1>
        <p className="text-gray-600 mb-6">
          The requested test could not be found.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{test.title}</h1>
            <p className="text-gray-600">{test.description}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary-600">
              {formatTime(timeLeft)}
            </div>
            <div className="text-sm text-gray-500">Time Remaining</div>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <span className="text-2xl">{getSectionIcon(currentSection.section_type)}</span>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {currentSection.title}
              </h2>
              <p className="text-gray-600">{currentSection.description}</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {currentSection.questions.length}
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Question {currentQuestion.question_number}
          </h3>
          
          {/* Reading Passage */}
          {currentQuestion.passage_text && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Reading Passage:</h4>
              <p className="text-gray-700 leading-relaxed">
                {currentQuestion.passage_text}
              </p>
            </div>
          )}

          {/* Question Text */}
          <p className="text-gray-800 mb-4">{currentQuestion.question_text}</p>

          {/* Audio Player for Listening */}
          {currentQuestion.audio_file_path && (
            <div className="mb-4">
              <audio controls className="w-full">
                <source src={currentQuestion.audio_file_path} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          {/* Answer Options */}
          {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options.length > 0 && (
            <div className="space-y-3">
              {currentQuestion.options.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={`question_${currentQuestion.id}`}
                    value={option.option_letter}
                    checked={answers[currentQuestion.id] === option.option_letter}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    className="mr-3"
                  />
                  <span className="font-semibold mr-2">{option.option_letter})</span>
                  <span>{option.option_text}</span>
                </label>
              ))}
            </div>
          )}

          {/* Fill in the blank */}
          {currentQuestion.question_type === 'fill_blank' && (
            <div>
              <input
                type="text"
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter your answer here..."
              />
            </div>
          )}

          {/* Essay/Text Area */}
          {(currentQuestion.question_type === 'essay' || currentQuestion.question_type === 'speaking') && (
            <div>
              <textarea
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={8}
                placeholder="Enter your answer here..."
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex space-x-2">
            {currentQuestionIndex === currentSection.questions.length - 1 ? (
              currentSectionIndex === test.sections.length - 1 ? (
                <button
                  onClick={handleSubmitTest}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Test'}
                </button>
              ) : (
                <button
                  onClick={handleNextSection}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next Section
                </button>
              )
            ) : (
              <button
                onClick={handleNextQuestion}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span>
            {currentSectionIndex + 1} of {test.sections.length} sections
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${((currentSectionIndex * 100 + (currentQuestionIndex + 1) * 100 / currentSection.questions.length) / test.sections.length)}%`
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default IELTSTest;
